<template>
  <Transition name="fade">
    <Dialog
      v-if="isOpen"
      title="Copy Order"
      :showCloseButton="true"
      :showFooter="true"
      :showSaveButton="true"
      :showCancelButton="true"
      md
      @closeEvent="closeModal"
      @cancelEvent="closeModal"
      @saveEvent="submitCopyOrder"
      :saveButtonProps="{ disabled: false }"
      :saveButtonDisabled="isSubmitting"
    >
      <template #modal-content>
        <div class="flex flex-col gap-3">
          <!-- Header -->
          <div class="flex items-center justify-between border-b pb-2 mb-2">
            <h2 class="text-lg font-semibold">Select Products to Copy</h2>
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" v-model="selectAllItems" @change="toggleSelectAllItems" />
              <span class="text-sm font-medium">Select All Items</span>
            </label>
          </div>

          <!-- Items -->
          <div v-for="(item, itemIndex) in order.items" :key="item.id" class="w-full">
            <Accordion :default-open="true">
              <template #header>
                <div class="flex items-center gap-3 w-full">
                  <input
                    type="checkbox"
                    v-model="itemSelections[item.id].selectAll"
                    class="w-4 h-4 text-primary"
                    @change="toggleItemSelectAll(item)"
                    @click="(e) => e.stopPropagation()"
                  />
                  <!--
                    `item_no` is the public-facing item number — the order
                    number for a solo item, `<order_no>-1`, `-2` for a split one
                    (see order_items_table.md). This printed `item.id`, the
                    database key, which is a different number that looks like
                    the same kind of number. Position is the fallback rather
                    than the id: it is honest, it is what the reader can see on
                    screen anyway, and it can never be mistaken for a number
                    that identifies something else (AB#21223).
                  -->
                  <span class="font-medium" data-test="copy-order-item-label">
                    {{ item.item_no ? `Item ${item.item_no}` : `Item ${itemIndex + 1}` }}
                  </span>
                </div>
              </template>

              <template #content>
                <div class="grid grid-cols-3 gap-4 mt-2">
                  <div
                    v-for="fp in item.factory_products"
                    :key="fp.id"
                    @click="toggleProductSelection(item.id, fp.id)"
                    class="relative cursor-pointer border rounded-lg p-2 flex flex-col items-center transition-all duration-150"
                    :class="{
                      'border-primary bg-primary/10': itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                      'border-line hover:border-primary/50': !itemSelections[
                        item.id
                      ].selectedProducts.includes(fp.id),
                    }"
                  >
                    <!-- Checkbox (optional small corner one) -->
                    <input
                      type="checkbox"
                      :checked="itemSelections[item.id].selectedProducts.includes(fp.id)"
                      class="absolute top-2 left-2 w-4 h-4 text-primary pointer-events-none"
                    />

                    <!-- Product Info -->
                    <div class="grid grid-cols-2 gap-2 w-full">
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.front_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')]
                          "
                          :src="storage_url + '/' + fp.front_image"
                          alt="front"
                          class="size-[100px] object-cover rounded-md border border-line"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'f')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-line bg-surface"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-ink-soft">Front</span>
                      </div>
                      <div class="flex flex-col items-center">
                        <img
                          v-if="
                            fp.back_image &&
                            !copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')]
                          "
                          :src="storage_url + '/' + fp.back_image"
                          alt="back"
                          class="size-[100px] object-cover rounded-md border border-line"
                          @error="
                            copyOrderFpThumbFailed[copyOrderThumbKey(item.id, fp.id, 'b')] = true
                          "
                        />
                        <div
                          v-else
                          class="size-[100px] shrink-0 rounded-md border border-line bg-surface"
                          aria-hidden="true"
                        />
                        <span class="text-xs mt-1 text-ink-soft">Back</span>
                      </div>
                    </div>

                    <p class="text-sm font-medium mt-2 text-center">
                      <!--
                        The SKU number when the product has no name, not the
                        row's database id: a SKU is something the reader can
                        look up, and `#${fp.id}` was a number nobody can act on
                        dressed as one they can (AB#21223).
                      -->
                      {{
                        (fp.new_product_name ? fp.new_product_name : fp.product_name) ||
                        (fp.sku_number ? `SKU ${fp.sku_number}` : 'Unnamed product')
                      }}
                    </p>
                  </div>
                </div>
              </template>
            </Accordion>
          </div>
        </div>
      </template>
    </Dialog>
  </Transition>
</template>

<script setup lang="ts">
import Accordion from '@/components/Shared/Accordion.vue';
import Dialog from '@/components/Shared/Dialog.vue';
import { showAlert, showToast } from '@/composables/swal';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { CopyOrderResponseData, OrderAdminAssignment, OrderDetail, OrderDetailItem } from '@/models';
import { reactive, ref } from 'vue';

const storage_url = import.meta.env.VITE_APP_FILE_URL;
const props = defineProps<{ order: OrderDetail }>();
const isSubmitting = ref(false);

const isOpen = ref(true);
const selectAllItems = ref(false);
const itemSelections = reactive<
  Record<string, { selectAll: boolean; selectedProducts: (string | number)[] }>
>({});

/** Failed thumb loads → empty slot (no `/img/placeholder.png`). */
const copyOrderFpThumbFailed = reactive<Record<string, boolean>>({});
function copyOrderThumbKey(
  itemId: string | number,
  fpId: string | number,
  side: 'f' | 'b',
): string {
  return `${itemId}-${fpId}-${side}`;
}

const emit = defineEmits(['close', 'save']);

// ✅ Initialize selections before render
if (props.order?.items?.length) {
  props.order.items.forEach((item) => {
    itemSelections[item.id] = {
      selectAll: false,
      selectedProducts: [],
    };
  });
}

/* -------------------------------------------------------------------------- */
/* ✅ FIX: Explicit "Select All Items" toggle instead of watcher               */
/* -------------------------------------------------------------------------- */
const toggleSelectAllItems = () => {
  const newVal = selectAllItems.value;
  props.order.items.forEach((item) => {
    const itemSel = itemSelections[item.id];
    if (!itemSel) return;
    itemSel.selectAll = newVal;
    itemSel.selectedProducts = newVal ? item.factory_products.map((fp) => fp.id) : [];
  });
};

/* -------------------------------------------------------------------------- */
const toggleItemSelectAll = (item: OrderDetailItem) => {
  const itemSel = itemSelections[item.id];
  if (!itemSel) return;
  itemSel.selectedProducts = itemSel.selectAll ? item.factory_products.map((fp) => fp.id) : [];

  // 🧠 Sync global "Select All Items" if needed
  updateGlobalSelectAll();
};

const toggleProductSelection = (itemId: string | number, productId: string | number) => {
  const itemSel = itemSelections[itemId];
  if (!itemSel) return;
  const index = itemSel.selectedProducts.indexOf(productId);
  if (index === -1) {
    itemSel.selectedProducts.push(productId);
  } else {
    itemSel.selectedProducts.splice(index, 1);
  }

  // auto toggle "select all" for that item
  const total = props.order.items.find((i) => i.id === itemId)?.factory_products.length || 0;
  itemSel.selectAll = itemSel.selectedProducts.length === total;

  // 🧠 Sync global "Select All Items"
  updateGlobalSelectAll();
};

// 🧩 Keep top-level selectAllItems reactive (without triggering resets)
const updateGlobalSelectAll = () => {
  const allSelected = props.order.items.every((item) => itemSelections[item.id]?.selectAll);
  selectAllItems.value = allSelected;
};

/* -------------------------------------------------------------------------- */
const buildPayload = () => {
  if (selectAllItems.value) return {};
  const map: Record<string, (string | number)[]> = {};
  for (const item of props.order.items) {
    const selected = itemSelections[item.id]?.selectedProducts ?? [];
    if (selected.length > 0) {
      map[item.id] = selected;
    }
  }
  return { order_item_factory_products_map: map };
};

/**
 * Builds the warning message for a copied order that ended up with no
 * Order Admin. Returns `null` when the backend response doesn't indicate a
 * missing-admin condition (either `assigned: true` or a legacy response
 * without the `order_admin_assignment` key at all).
 */
function buildNoOrderAdminWarning(
  assignment: OrderAdminAssignment | undefined
): string | null {
  if (!assignment || assignment.assigned) return null;

  let body: string;
  if (assignment.alert_reason === 'original_admin_inactive') {
    body =
      "The copied order was created without an Order Admin because the original order's admin is no longer active.";
  } else if (assignment.alert_reason === 'original_unassigned') {
    body =
      'The copied order was created without an Order Admin because the original order had none assigned.';
  } else {
    body = 'The copied order was created without an Order Admin.';
  }

  if (assignment.alert_sent) {
    body += ' An email alert has been sent to the configured recipients.';
  }

  return body;
}

const submitCopyOrder = async () => {
  isSubmitting.value = true;
  const payload = buildPayload();
  try {
    const resp = await http.post<CopyOrderResponseData>(
      `/copy-order/${props.order.id}`,
      Object.keys(payload).length ? payload : {}
    );
    showToast(resp.data.message || 'Order copied successfully!', 'success');
    emit('save', true);
    closeModal();

    const warningMessage = buildNoOrderAdminWarning(resp.data.result?.order_admin_assignment);
    if (warningMessage) {
      showAlert('No Order Admin assigned', warningMessage, 'warning');
    }
  } catch (err: any) {
    handleResponseException(err);
    isSubmitting.value = false;
  }
};

const closeModal = () => {
  emit('close');
};
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
